<template>
    <div
        class="flex flex-col flex-1 min-h-[70vh] md:min-h-[600px] h-full"
        v-show="visible"
    >
        <!-- Main Container -->
        <div
            class="flex w-full p-6 flex-col items-center gap-8 rounded-none md:rounded-lg bg-white dark:bg-slate-800 pb-24 md:pb-6 flex-1 min-h-0 overflow-y-auto md:overflow-visible lg:overflow-y-auto"
        >
            <!-- Header -->
            <div class="flex items-start justify-between self-stretch">
                <div
                    class="flex flex-col justify-center items-start gap-1 flex-1"
                >
                    <h2
                        class="flex-1 text-slate-800 dark:text-slate-100 text-xl font-medium leading-7"
                    >
                        Add account
                    </h2>
                    <p class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5">
                        {{ subtitle || 'Select bank to connect' }}
                    </p>
                </div>
                <a
                    href="#"
                    class="btn-icon btn-transparent w-10"
                    role="button"
                    @click.prevent="$emit('close')"
                >
                    <Icon icon="close" />
                </a>
            </div>

            <!-- Search -->
            <div class="flex flex-col items-start self-stretch px-2">
                <UiInput
                    id="search_bank"
                    name="search_bank"
                    :options="{
                        wrapperClass: 'w-full',
                        labelInputWrapperClass: 'w-full',
                    }"
                >
                    <template #input>
                        <div class="relative w-full">
                            <div
                                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                            >
                                <Icon
                                    icon="search"
                                    class="w-5 h-5 text-slate-400"
                                />
                            </div>
                            <input
                                :value="bankSearchQuery"
                                @input="
                                    $emit(
                                        'update:bankSearchQuery',
                                        $event.target.value
                                    )
                                "
                                type="text"
                                placeholder="Search bank"
                                class="form-input w-full pl-10"
                            />
                        </div>
                    </template>
                </UiInput>
            </div>

            <!-- Banks list -->
            <div
                class="flex flex-col items-start self-stretch overflow-y-auto w-full max-h-[calc(100dvh-320px)] md:max-h-none md:flex-1 md:min-h-0 lg:max-h-80 lg:flex-none py-2 px-2"
            >
                <button
                    v-for="bank in banks"
                    :key="'bank-' + (bank.id || bank.institution_code)"
                    type="button"
                    class="flex w-full items-center gap-4 py-4 px-3 border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-left rounded-md"
                    :class="{
                        'bg-slate-50 dark:bg-slate-700/50':
                            activeBank &&
                            activeBank.institution_code ===
                                bank.institution_code,
                    }"
                    @click="$emit('selectBank', bank)"
                >
                    <img
                        :src="bank.institution?.small_logo_url"
                        :alt="bank.name"
                        class="rounded-full w-12 h-12"
                    />
                    <span class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">
                        {{ bank.name }}
                    </span>
                    <div
                        v-if="
                            activeBank &&
                            activeBank.institution_code ===
                                bank.institution_code
                        "
                        class="ml-auto flex items-center justify-center w-6 h-6 rounded-full bg-[#FD421D] text-white"
                    >
                        <Icon icon="check" :width="24" :height="24" />
                    </div>
                </button>
            </div>

            <div class="w-full" v-show="errors.length > 0">
                <Errors :errors="errors" />
            </div>
        </div>

        <!-- Footer (desktop only) -->
        <div
            class="hidden md:flex justify-between items-center p-6 bg-white dark:bg-slate-800 flex-shrink-0 mt-auto w-full"
        >
            <button
                type="button"
                @click.prevent="$emit('back')"
                class="flex py-2.5 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                id="hs-wallet-connect-back"
            >
                Back
            </button>
            <div class="flex items-start gap-2">
                <button
                    type="button"
                    @click.prevent="$emit('close')"
                    class="flex py-2.5 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                    Cancel
                </button>
                <button
                    @click.prevent="$emit('next')"
                    :disabled="!activeBank"
                    class="flex py-2.5 px-4 justify-center items-center gap-2 rounded bg-slate-800 dark:bg-slate-600 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-500 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed"
                    id="hs-wallet-connect-next"
                >
                    Next
                </button>
            </div>
        </div>
        <!-- Mobile bottom bar: Back/Next only -->
        <div
            class="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] z-[1000]"
        >
            <div class="grid grid-cols-2 gap-2">
                <button
                    type="button"
                    @click.prevent="$emit('back')"
                    class="w-full py-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700"
                    id="hs-wallet-connect-banklist-back-mobile"
                >
                    Back
                </button>
                <button
                    type="button"
                    @click.prevent="$emit('next')"
                    :disabled="!activeBank"
                    class="w-full py-3 rounded-md bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-500 disabled:bg-slate-400 disabled:cursor-not-allowed"
                    id="hs-wallet-connect-banklist-next-mobile"
                >
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import Errors from '@/components/errors.vue';

defineProps({
    visible: { type: Boolean, default: true },
    banks: { type: Array, required: true },
    activeBank: { type: Object, default: null },
    bankSearchQuery: { type: String, default: '' },
    errors: { type: Array, default: () => [] },
    subtitle: { type: String, default: '' },
});

defineEmits(['selectBank', 'back', 'next', 'close', 'update:bankSearchQuery']);
</script>
